<template>
  <v-card
    v-if="lead && items.length"
    border
  >
    <v-timeline
      align="top"
      density="compact"
    >
      <template
        v-for="(item, index) in items"
        :key="index"
      >
        <component
          :is="component(item)"
          :data="item"
          :lead-status="lead.currentStatus"
          :lead-id="lead.id"
          :lead-crm-link="lead.crmLink"
          :lead-origin="lead.origin"
        />
      </template>
    </v-timeline>
  </v-card>
</template>

<script>
import { sortByDate } from '@/lib/sort-functions'
import EmailActivity from '@/modules/leads/Lead/Timeline/EmailActivity.vue'
import { TIMELINE_FILTERS } from '@/modules/leads/Lead/Timeline/helpers/filters.js'
import NewsletterInvitation from '@/modules/leads/Lead/Timeline/NewsletterInvitation.vue'

import ChannelActivity from './ChannelActivity.vue'
import TimelineCommentary from './Commentary'
import TimelineCrmLeadDeleted from './CrmLeadDeleted'
import TimelineCrmLeadImported from './CrmLeadImported.vue'
import TimelineDownload from './Download'
import TimelineEmailActivity from './EmailActivity'
import TimelineExport from './Export.vue'
import TimelineExportToCrm from './ExportToCrm.vue'
import TimelineInAppValuation from './InAppValuation.vue'
import TimelineInvitation from './Invitation.vue'
import TimelineKonstantine from './Konstantine.vue'
import TimelineLeadImported from './LeadImported'
import TimelineLeadStatus from './LeadStatus'
import TimelineLeadUpdate from './LeadUpdate.vue'
import TimelineLeadValuation from './LeadValuation.vue'
import TimelineLifeAnnuity from './LifeAnnuity'
import TimelineMessage from './Message'
import TimelinePartialSale from './PartialSale'
import TimeLinePlotValuation from './PlotValuation'
import TimelinePropertyRequest from './PropertyRequest'
import TimelineQuizzard from './Quizzard.vue'
import TimelineReturnCall from './ReturnCall'
import TimelineValuation from './Valuation'

export default {
  components: {
    EmailActivity,
    TimelineDownload,
    TimelineValuation,
    TimelineReturnCall,
    TimelineLifeAnnuity,
    TimelinePartialSale,
    TimeLinePlotValuation,
    TimelineLeadStatus,
    TimelineMessage,
    TimelineEmailActivity,
    TimelineCommentary,
    TimelineLeadImported,
    TimelineQuizzard,
    TimelineInvitation,
    TimelineKonstantine,
  },
  props: {
    lead: { type: Object, default: null },
    timelineFilter: { type: Object, required: false },
  },
  computed: {
    items() {
      const { statusHistory = [], messages = [], commentaries = [] } = this.lead || {}

      const baseActivities = this.lead?.activities ?? []
      const activities = [
        ...baseActivities,
        {
          __typename: 'LeadActivity',
          type: 'EMAIL_NEXT',
          createdAt: null,
          initiator: null,
          details: {
            funnelUnsubscribed: baseActivities.find((a) => a?.type === 'FUNNEL_UNSUBSCRIBED'),
            channelUnsubscribed: baseActivities.find((a) => a?.type === 'CHANNEL_UNSUBSCRIBED'),
            emailOpened: baseActivities.find((a) => a?.type === 'EMAIL_OPENED'),
          },
        },
      ]

      const filters = {
        ALL: [statusHistory, messages, activities, commentaries],
        EMAILS: [messages, activities.filter((a) => a?.type?.startsWith('EMAIL_'))],
        COMMENTS: [commentaries],
        SYSTEM_INFO: [statusHistory, activities.filter((a) => a?.initiator === 'SYSTEM')],
        ACTIONS: [activities.filter((a) => a?.initiator === 'LEAD')],
      }

      const active = Object.keys(this.timelineFilterLocal).filter(
        (k) => this.timelineFilterLocal[k]?.isActive
      )

      const keys = active.includes('ALL') ? ['ALL'] : active

      const list = keys.flatMap((k) => filters[k] || []).flat()

      const fallbackSort = (a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0)
      const base = sortByDate?.('createdAt', 'desc') ?? fallbackSort

      // EMAIL_NEXT first, preserve their order; others sorted by `base`
      const byEmailNextFirst = (a, b) => {
        const aNext = a.type === 'EMAIL_NEXT'
        const bNext = b.type === 'EMAIL_NEXT'
        if (aNext && bNext) return 0
        if (aNext) return -1
        if (bNext) return 1
        return base(a, b)
      }

      return list.slice().sort(byEmailNextFirst)
    },
  },
  data() {
    return {
      timelineFilterLocal: {
        [TIMELINE_FILTERS.ALL]: { isActive: true },
        [TIMELINE_FILTERS.SYSTEM_INFO]: { isActive: false },
        [TIMELINE_FILTERS.EMAILS]: { isActive: false },
        [TIMELINE_FILTERS.COMMENTS]: { isActive: false },
        [TIMELINE_FILTERS.ACTIONS]: { isActive: false },
      },
    }
  },
  methods: {
    component({ __typename, type, details }) {
      return {
        StatusHistory: () => TimelineLeadStatus,
        Message: () => TimelineMessage,
        Commentary: () => TimelineCommentary,
        LeadActivity: () =>
          ({
            EXPORT: () => TimelineExport,
            CRM_LEAD_DELETED: () => TimelineCrmLeadDeleted,
            CRM_LEAD_IMPORTED: () => TimelineCrmLeadImported,
            LEAD_DATA_UPDATED: () => TimelineLeadUpdate,
            EXPORT_TO_CRM: () => TimelineExportToCrm,
            CONVERSION: () =>
              ({
                VALUATION: TimelineLeadValuation,
                RETURN_CALL: TimelineReturnCall,
                DOWNLOAD: TimelineDownload,
                LIFE_ANNUITY: TimelineLifeAnnuity,
                PARTIAL_SALE: TimelinePartialSale,
                PROPERTY_REQUEST: TimelinePropertyRequest,
                PLOT_VALUATION: TimeLinePlotValuation,
              })[details[type]?.conversionType],
            FUNNEL_UNSUBSCRIBED: () => ChannelActivity,
            CHANNEL_UNSUBSCRIBED: () => ChannelActivity,
            CHANNEL_SUBSCRIBED: () => ChannelActivity,
            CHANNEL_RESUBSCRIBED: () => ChannelActivity,
            CHANNEL_PAUSED: () => ChannelActivity,
            CHANNEL_RESUMED: () => ChannelActivity,
            EMAIL_SENT: () => TimelineEmailActivity,
            EMAIL_OPENED: () => TimelineEmailActivity,
            EMAIL_SPAM_COMPLAINT: () => TimelineEmailActivity,
            EMAIL_NEXT: () => TimelineEmailActivity,
            EMAIL_BOUNCED: () => TimelineEmailActivity,
            LEAD_IMPORTED: () => TimelineLeadImported,
            INVITATION: () => TimelineInvitation,
            KONSTANTINE: () => TimelineKonstantine,
            NEWSLETTER_INVITATION: () => NewsletterInvitation,
            IN_APP_VALUATION: () => TimelineInAppValuation,
            PRE_CONVERSION: () =>
              ({
                QUIZ: TimelineQuizzard,
              })[details[type]?.preConversionType],
          })[type](),
      }[__typename]()
    },
  },
  watch: {
    timelineFilter: {
      handler(newVal) {
        if (newVal) {
          this.timelineFilterLocal = { ...newVal }
        }
      },
      deep: true,
      immediate: true,
    },
  },
}
</script>
<style scoped>
:deep(.v-timeline-item__body) {
  width: 100%;
}

:deep(.v-timeline) {
  position: relative;
}

:deep(.v-timeline-divider__inner-dot, .v-icon) {
  color: #fff !important;
}

:deep(.v-timeline-divider__dot) {
  margin-left: 28px;
}

:deep(.v-timeline::before) {
  content: '';
  position: absolute;
  top: 0;
  height: 100%;
  width: var(--v-timeline-line-thickness);
  background: rgba(var(--v-border-color), var(--v-border-opacity));
  left: calc(28px + (38px / 2));
}
</style>
